/**
 * 办公室布局配置器（DIY 柜子）：左侧画布槽位 + 右侧 SKU 选购区。
 *
 * 数据：`.office-layout-configurator__data` 内 JSON（products、variants、maxSlots、文案等）。
 * 流程：选颜色 → 列出变体卡片 → 拖入画布或点击空槽打开弹窗选款。
 * 槽位：`slots[]` 存放已放置项；多槽时 `sideCabinetOnly`（边柜）仅能放在视觉左/右两侧
 *（物理 0 / maxSlots-1，或左紧凑后最右已填槽 / 紧邻右扩）。
 * 购物车：按槽位生成 line item，带 combine 相关 properties 用于加购/结算。
 *
 * 外部：`office-layout-configurator:apply-preset` 事件会调用 `root._olcApplyPreset(detail)`。
 */
(function () {
  /** HTML5 拖拽使用的 MIME（右侧卡片与画布槽位之间传递变体 JSON）。 */
  const MIME = 'application/x-office-layout-variant';
  const CROSS_PAGE_PRESET_STORAGE_KEY = 'olc_cross_page_preset';
  const CROSS_PAGE_PRESET_MAX_AGE = 30 * 60 * 1000;

  /**
   * 自定义拖拽图：在视口外生成完整节点，避免「SKU 区滚动后」仅对可见区域截图，
   * 导致幽灵图混入主图以外的像素（如上方标题等）。
   */
  function setOlcVariantDragImageFromClone(img, e, imgWrap) {
    var useEl = img && img.naturalWidth ? img : imgWrap;
    if (!useEl) return;
    var rect = useEl.getBoundingClientRect();
    if (rect.width < 1 || rect.height < 1) return;
    var offX = e.clientX - rect.left;
    var offY = e.clientY - rect.top;
    var ghost;
    if (useEl === img) {
      ghost = img.cloneNode(true);
      ghost.removeAttribute('loading');
      if (img.currentSrc) ghost.src = img.currentSrc;
      else if (img.src) ghost.src = img.src;
      ghost.style.cssText =
        'position:fixed;left:0;top:-10000px;width:' +
        rect.width +
        'px;height:' +
        rect.height +
        'px;object-fit:contain;object-position:center;pointer-events:none;margin:0;padding:0;border:0;';
    } else {
      ghost = imgWrap.cloneNode(true);
      ghost.querySelectorAll('img').forEach(function (g) {
        g.removeAttribute('loading');
      });
      ghost.style.cssText =
        'position:fixed;left:0;top:-10000px;width:' +
        rect.width +
        'px;height:' +
        rect.height +
        'px;box-sizing:border-box;pointer-events:none;margin:0;padding:0;border:0;overflow:hidden;';
    }
    document.body.appendChild(ghost);
    try {
      e.dataTransfer.setDragImage(ghost, offX, offY);
    } catch (err) {
      void err;
    } finally {
      window.setTimeout(function () {
        if (ghost.parentNode) {
          ghost.parentNode.removeChild(ghost);
        }
      }, 0);
    }
  }

  // --- 模块级工具：id、金额、购物车 payload、变体文案等 ---

  function generateId() {
    return String(Math.random().toFixed(10) * 1e10) + Date.now();
  }

  function formatMoney(cents, format) {
    if (typeof theme !== 'undefined' && theme.Currency && typeof theme.Currency.formatMoney === 'function') {
      return theme.Currency.formatMoney(cents, format);
    }
    return (cents / 100).toFixed(2);
  }

  /** 从类似 "48W" 的文案中解析宽度数字（兼容旧数据）。 */
  function parseWidthFromLabel(dimStr) {
    if (!dimStr) return '';
    var m = String(dimStr).match(/(\d+(?:\.\d+)?)\s*W/i);
    return m ? m[1] : '';
  }

  /** 变体文案中第一个数值；无则 null。例 40W*190H*35.6D → "40"。 */
  function parseFirstNumberFromVariantLabel(str) {
    if (str == null || str === '') return null;
    var m = String(str).match(/(\d+(?:\.\d+)?)/);
    return m ? m[1] : null;
  }

  function normalizeWidthToken(raw) {
    if (raw == null || raw === '') return '';
    var n = parseFloat(String(raw), 10);
    if (isNaN(n)) return String(raw);
    if (n % 1 === 0) return String(Math.round(n));
    return String(raw);
  }

  function widthLabelFromVariantTitle(title) {
    var p = parseFirstNumberFromVariantLabel(title);
    return p == null ? '' : normalizeWidthToken(p);
  }

  /** .sku-width 展示：无数字为 "null"。 */
  function getSlotWidthDisplayText(slot) {
    if (!slot) return 'null';
    var w = slot.widthLabel;
    if (w != null && String(w).trim() !== '') return String(w).trim();
    var p = parseFirstNumberFromVariantLabel(slot.variantTitle || slot.title || '');
    if (p == null) return 'null';
    return normalizeWidthToken(p);
  }

  function slotWidthNumericForSum(slot) {
    if (!slot) return 0;
    var w = slot.widthLabel;
    if (w != null && String(w).trim() !== '') {
      var n = parseFloat(String(w), 10);
      return isNaN(n) ? 0 : n;
    }
    var p = parseFirstNumberFromVariantLabel(slot.variantTitle || slot.title || '');
    if (p == null) return 0;
    var n2 = parseFloat(p, 10);
    return isNaN(n2) ? 0 : n2;
  }

  /** 每个已填槽位对应一行购物车项；共用 combineId 将整套配置归为一组。 */
  function buildLineItems(slots, sharedCombineId) {
    return slots
      .filter(Boolean)
      .map(function (slot) {
        var props = {
          _combineConfig: {
            combineId: sharedCombineId,
            combineType: 'product',
            itemType: 'product',
          },
          _version: window.commentPropertiesVersion != null ? window.commentPropertiesVersion : 1,
          _productId: slot.productId,
          _actualProductId: slot.productId,
          _skuId: slot.variantId,
          _productCombineId: generateId(),
          _fulfillmentGroupId: generateId(),
        };
        return {
          id: slot.variantId,
          quantity: 1,
          properties: props,
        };
      });
  }

  /** 主题支持 mini-cart 时附带 sections，用于刷新购物车抽屉。 */
  function cartPayload(items) {
    var body = { items: items };
    var miniCart = document.querySelector('mini-cart');
    if (miniCart && typeof miniCart.getSectionsToRender === 'function') {
      body.sections = miniCart
        .getSectionsToRender()
        .map(function (s) {
          return s.section;
        })
        .join(',');
    }
    body.sections_url = window.location.pathname;
    return body;
  }

  /** 变体展示名（去掉颜色选项），用于尺寸与标签。 */
  function variantLabel(v, colorIndex) {
    var parts = [];
    for (var i = 0; i < 3; i++) {
      if (i === colorIndex) continue;
      var o = v.options[i];
      if (o === null || o === undefined) continue;
      var s = String(o).trim();
      if (s) parts.push(s);
    }
    return parts.length ? parts.join(' · ') : '';
  }

  function optionValueAt(v, idx) {
    if (!v || !v.options || idx < 0 || idx > 2) return '';
    var o = v.options[idx];
    if (o === null || o === undefined) return '';
    return String(o).trim();
  }

  function countDistinctOptionValues(idx, rows) {
    var seen = {};
    var n = 0;
    rows.forEach(function (row) {
      var c = optionValueAt(row.variant, idx);
      if (!c) return;
      if (!seen[c]) {
        seen[c] = true;
        n++;
      }
    });
    return n;
  }

  /** Liquid 未传固定色板时，推断哪一个 option 是「颜色」。 */
  function resolveColorOptionIndex(preferred, flatRows) {
    if (countDistinctOptionValues(preferred, flatRows) > 0) return preferred;
    for (var i = 0; i < 3; i++) {
      if (countDistinctOptionValues(i, flatRows) > 0) return i;
    }
    return preferred;
  }

  /**
   * 初始化单个 section：读取 config、绑定 DOM、渲染色块 / 变体列表 / 画布。
   */
  function initConfigurator(root) {
    var dataEl = root.querySelector('.office-layout-configurator__data');
    if (!dataEl) return;
    var config;
    try {
      config = JSON.parse(dataEl.textContent);
    } catch (e) {
      return;
    }

    var maxSlots = Math.max(1, Math.min(5, config.maxSlots || 3));
    var initialSlotIndex = Math.max(0, Math.floor((maxSlots - 1) / 2));
    var moneyFormat =
      config.moneyFormat ||
      (window.shopSettings && window.shopSettings.moneyFormat) ||
      '${{amount}}';
    var products = config.products || [];
    var hasSideCabinetProduct = !!config.hasSideCabinetProduct;
    var urlStripQueryAfterApply = config.urlStripQueryAfterApply !== false;
    var urlAutoPlaceFirstSlot = !!config.urlAutoPlaceFirstSlot;

    var slotsRoot = root.querySelector('[data-olc-slots-root]');
    var canvasStageEl = root.querySelector('[data-olc-canvas-stage]');
    var swatchEl = root.querySelector('[data-olc-swatches]');
    var variantsEl = root.querySelector('[data-olc-variants]');
    var colorCurrentEl = root.querySelector('[data-olc-color-current]');
    if (!slotsRoot || !swatchEl || !variantsEl) return;
    var priceEl = root.querySelector('[data-olc-price]');
    var priceNoteEl = root.querySelector('[data-olc-price-note]');
    var addBtn = root.querySelector('[data-olc-add-to-cart]');
    var buyBtn = root.querySelector('[data-olc-buy-configuration]');
    var dimensionTotalEl = root.querySelector('[data-olc-dimension-total]');
    var canvasEl = root.querySelector('.office-layout-configurator__canvas');
    var slotFootersRowEl = root.querySelector('[data-olc-slot-footers-row]');
    var slotFootersGridEl = root.querySelector('[data-olc-slot-footers]');
    var editModalEl = root.querySelector('[data-olc-edit-modal]');
    var modalHeadingEl = root.querySelector('[data-olc-modal-heading]');
    var modalTitleBaseEl = root.querySelector('[data-olc-modal-title-base]');
    var modalTitleCurrentEl = root.querySelector('[data-olc-modal-title-current]');
    var modalSwatchesEl = root.querySelector('[data-olc-modal-swatches]');
    var modalColorCurrentEl = root.querySelector('[data-olc-modal-color-current]');
    var modalProductLineEl = root.querySelector('[data-olc-modal-product-line]');
    var modalVariantsEl = root.querySelector('[data-olc-modal-variants]');
    var modalConfirmBtn = root.querySelector('[data-olc-modal-confirm]');
    var maxPopoverEl = root.querySelector('[data-olc-max-popover]');
    var dragTipEl = root.querySelector('[data-olc-drag-tip]');

    /** 打开弹窗时锁定页面滚动（引用计数，支持嵌套打开/关闭）。 */
    var olcBodyScrollLockCount = 0;
    var olcSavedScrollY = 0;

    function lockOlcBodyScroll() {
      if (olcBodyScrollLockCount === 0) {
        olcSavedScrollY = window.scrollY || window.pageYOffset || 0;
        document.documentElement.classList.add('olc-scroll-lock');
        document.body.style.position = 'fixed';
        document.body.style.top = '-' + olcSavedScrollY + 'px';
        document.body.style.left = '0';
        document.body.style.right = '0';
        document.body.style.width = '100%';
      }
      olcBodyScrollLockCount++;
    }

    function unlockOlcBodyScroll() {
      if (olcBodyScrollLockCount <= 0) return;
      olcBodyScrollLockCount--;
      if (olcBodyScrollLockCount === 0) {
        document.documentElement.classList.remove('olc-scroll-lock');
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.left = '';
        document.body.style.right = '';
        document.body.style.width = '';
        window.scrollTo(0, olcSavedScrollY);
      }
    }

    // --- 运行时状态：当前颜色、弹窗、画布槽位 ---

    var selectedColor = null;
    var modalColor = null;
    var modalSlotIndex = null;
    var modalProductId = null;
    var modalSelectedVariantId = null;
    var modalMode = 'edit';
    var modalPickSlotIndex = null;
    /** 槽位数组；null 为空槽。未全满时 placementSeq 用于底部徽章序号排序。 */
    var slots = new Array(maxSlots).fill(null);
    var nextPlacementSeq = 1;

    /** 扁平列出所有变体行（含商品元数据），供筛选与排序。 */
    function allVariantsFlat() {
      var list = [];
      products.forEach(function (p) {
        (p.variants || []).forEach(function (v, vi) {
          list.push({
            variant: v,
            productId: p.id,
            productTitle: p.title,
            skuSectionHeading: typeof p.skuSectionHeading === 'string' ? p.skuSectionHeading : '',
            skuGroupPriority: p.skuGroupPriority != null ? Number(p.skuGroupPriority) : 10,
            blockOrder: p.blockOrder,
            variantIndex: vi,
            sideCabinetOnly: !!p.sideCabinetOnly,
          });
        });
      });
      return list;
    }

    var flatRows = allVariantsFlat();

    // 用户首次拖拽后移除顶部「拖拽提示」节点。
    if (dragTipEl && variantsEl) {
      variantsEl.addEventListener('dragstart', function (e) {
        if (!dragTipEl || !e.target.closest('.office-layout-configurator__variant-card')) return;
        dragTipEl.remove();
        dragTipEl = null;
      });
    }

    /** 排序：SKU 分组优先级 → 非边柜优先 → 区块顺序 → 变体序号。 */
    function compareCabinetSkuOrder(a, b) {
      var ap = a.skuGroupPriority != null ? Number(a.skuGroupPriority) : 10;
      var bp = b.skuGroupPriority != null ? Number(b.skuGroupPriority) : 10;
      if (ap !== bp) return ap - bp;
      if (!!a.sideCabinetOnly !== !!b.sideCabinetOnly) return a.sideCabinetOnly ? 1 : -1;
      if (a.blockOrder !== b.blockOrder) return a.blockOrder - b.blockOrder;
      return a.variantIndex - b.variantIndex;
    }

    function formatSkuSectionHeading(text) {
      var t = (text || '').trim();
      if (!t) return '';
      if (/[:\uFF1A]\s*$/.test(t)) return t;
      return t;
    }

    /** 与 __variants-sku-heading__base 配套：槽位底部文案展示时去掉半角/全角冒号。 */
    function stripColonsForSkuHeadingDisplay(text) {
      if (text == null || text === '') return '';
      var s = String(text).trim();
      if (!s) return '';
      var parts = s.split(/[:\uFF1A]+/);
      var out = [];
      for (var si = 0; si < parts.length; si++) {
        var p = String(parts[si] || '').trim();
        if (p) out.push(p);
      }
      return out.join(' ');
    }

    function resolveSkuSectionHeading(row) {
      var h = row.skuSectionHeading && String(row.skuSectionHeading).trim();
      if (h) return h;
      return (row.productTitle && String(row.productTitle).trim()) || '';
    }

    function shouldShowSkuSectionHeading(row, productsLength) {
      if (row.skuSectionHeading && String(row.skuSectionHeading).trim()) return true;
      return productsLength > 1;
    }

    /** 按商品聚合变体行，用于分组标题与网格。 */
    function groupRowsByProduct(rows) {
      var groups = [];
      var cur = null;
      rows.forEach(function (row) {
        var pid = String(row.productId);
        if (!cur || String(cur.productId) !== pid) {
          cur = {
            productId: row.productId,
            rows: [],
          };
          groups.push(cur);
        }
        cur.rows.push(row);
      });
      return groups;
    }

    var preferredColorIdx =
      config.colorOptionIndex !== undefined && config.colorOptionIndex !== null
        ? config.colorOptionIndex
        : 0;
    var hasLiquidColors = config.colors && config.colors.length;
    var colorIdx = hasLiquidColors
      ? preferredColorIdx
      : resolveColorOptionIndex(preferredColorIdx, flatRows);

    function uniqueColorsFromVariants() {
      var seen = {};
      var order = [];
      flatRows.forEach(function (row) {
        var v = row.variant;
        var c = optionValueAt(v, colorIdx);
        if (!c) return;
        var p = findProductById(row.productId);
        var imgUrl = resolveVariantImageUrl(v, p);
        if (!seen[c]) {
          seen[c] = { value: c, image: imgUrl || '', firstOrder: order.length };
          order.push(c);
        }
        if (!seen[c].image && imgUrl) seen[c].image = imgUrl;
      });
      return order.map(function (c) {
        return seen[c];
      });
    }

    function getColorPalette() {
      if (config.colors && config.colors.length) {
        return config.colors;
      }
      return uniqueColorsFromVariants().map(function (c) {
        return {
          value: c.value,
          swatchUrl: c.image || '',
          fallbackHandle: '',
        };
      });
    }

    function filteredVariants(color) {
      return flatRows
        .filter(function (row) {
          var v = row.variant;
          return optionValueAt(v, colorIdx) === String(color).trim();
        })
        .sort(compareCabinetSkuOrder);
    }

    /** 至少放入一件商品后为 true（多槽时从「收起占位」展开为整行槽位）。 */
    function canvasExpanded() {
      if (maxSlots <= 1) return true;
      return slots.some(Boolean);
    }

    /** 尚未放置任何商品：多槽时显示单个收起态拖放占位。 */
    function isCollapsedCanvas() {
      return maxSlots > 1 && !slots.some(Boolean);
    }

    /** 是否为物理两侧槽位：索引 0 或 maxSlots-1（画布右缘加号落点）。 */
    function isEdgeSlot(i) {
      if (i === null || i === undefined) return false;
      var n = typeof i === 'number' && !isNaN(i) ? i : parseInt(i, 10);
      if (isNaN(n)) return false;
      return n === 0 || n === maxSlots - 1;
    }

    /**
     * 边柜是否允许落在该槽：物理两侧，或左紧凑后当前填柜的视觉右缘 / 紧邻右扩一格。
     * 例 maxSlots=5 且为【边柜】【立柜】【立柜】时，索引 2 可 edit 换成边柜；
     * 仅认 isEdgeSlot 会误拦（右缘加号才是 maxSlots-1）。
     */
    function isSideCabinetAllowedSlot(i) {
      if (i === null || i === undefined) return false;
      var n = typeof i === 'number' && !isNaN(i) ? i : parseInt(i, 10);
      if (isNaN(n) || n < 0 || n >= maxSlots) return false;
      if (isEdgeSlot(n)) return true;
      var R = getRightmostFilledSlotIndex();
      if (R >= 0 && n === R) return true;
      if (!slots[n] && R >= 0 && n === R + 1) return true;
      return false;
    }

    /** 将已放置项向左紧凑，消除左侧空槽。 */
    function compactSlotsLeft() {
      var items = [];
      for (var i = 0; i < slots.length; i++) {
        if (slots[i]) items.push(slots[i]);
      }
      for (var j = 0; j < maxSlots; j++) {
        slots[j] = j < items.length ? items[j] : null;
      }
    }

    /** 画布已展开且无法再放置（满槽）。 */
    function isCanvasFull() {
      return !isCollapsedCanvas() && firstEmptyIndex() < 0;
    }

    function firstEmptyEdgeIndex() {
      for (var i = 0; i < slots.length; i++) {
        if (!slots[i] && isEdgeSlot(i)) return i;
      }
      return -1;
    }

    function notifySideCabinetEdgeOnly() {
      var msg =
        (config.strings && config.strings.sideCabinetEdgeOnly) ||
        'Side cabinets can only be placed on the left or right slot.';
      if (window.BMessage && typeof window.BMessage.warning === 'function') {
        window.BMessage.warning({ content: msg });
      } else if (window.BMessage && typeof window.BMessage.error === 'function') {
        window.BMessage.error({ content: msg });
      } else {
        alert(msg);
      }
    }

    function getRightmostFilledSlotIndex() {
      var r = -1;
      for (var i = 0; i < maxSlots; i++) {
        if (slots[i]) r = i;
      }
      return r;
    }

    /**
     * 向空槽填物时，是否会让当前「尾部」边柜右侧再增一物，从而左侧与右侧都有柜体、该边柜离开两侧槽位语义。
     * 典型：【立柜】【边柜】且右侧仍有空槽 → 再向右填成 [立,边,…]；或【边柜】【边柜】且右侧仍有空槽 → 再填成 [边,边,…]，
     * 尾索引边柜在数据上处于中间。
     */
    function fillingEmptyWouldTrapSideCabinetBetweenMains(slotIndex) {
      if (maxSlots <= 1 || slotIndex === null || slotIndex === undefined) return false;
      var e = typeof slotIndex === 'number' ? slotIndex : parseInt(slotIndex, 10);
      if (isNaN(e)) return false;
      var R = getRightmostFilledSlotIndex();
      if (R < 0 || !slots[R].sideCabinetOnly) return false;
      if (e <= R) return false;
      for (var j = 0; j < R; j++) {
        if (slots[j]) return true;
      }
      return false;
    }

    /**
     * 左侧加号 prepend：原槽 0 右移到索引 1；若槽 0 为边柜且索引 1 非两侧，边柜会落在中间。
     */
    function prependWouldDisplaceSideCabinetFromEdge() {
      return !!(slots[0] && slots[0].sideCabinetOnly && !isEdgeSlot(1));
    }

    function notifySideCabinetCannotBeMiddle() {
      var msg =
        (config.strings && config.strings.sideCabinetEdgeOnly) ||
        'Side cabinets can only be placed on the left or right slot.';
      if (window.BMessage && typeof window.BMessage.warning === 'function') {
        window.BMessage.warning({ content: msg });
      } else if (window.BMessage && typeof window.BMessage.error === 'function') {
        window.BMessage.error({ content: msg });
      } else {
        alert(msg);
      }
    }

    function setSwatchCssVars(btn, swatchUrl, fallbackHandle) {
      var u = (swatchUrl || '').trim();
      if (u) {
        btn.style.setProperty('--swatch-background-image', 'url("' + u.replace(/"/g, '\\"') + '")');
      } else {
        btn.style.setProperty('--swatch-background-image', 'none');
      }
      btn.style.setProperty('--swatch-background-color', fallbackHandle || 'transparent');
    }

    function renderSwatches() {
      var palette = getColorPalette();
      if (!palette.length) {
        swatchEl.innerHTML = '';
        if (colorCurrentEl) colorCurrentEl.textContent = '';
        return;
      }
      if (!selectedColor || !palette.some(function (c) {
        return c.value === selectedColor;
      })) {
        selectedColor = palette[0].value;
      }
      swatchEl.innerHTML = '';
      palette.forEach(function (c) {
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.className =
          'office-layout-configurator__color-swatch color__swatch' +
          (c.value === selectedColor ? ' is-active' : '');
        btn.setAttribute('aria-label', c.value);
        btn.dataset.color = c.value;
        setSwatchCssVars(btn, c.swatchUrl, c.fallbackHandle);
        var tip = document.createElement('span');
        tip.className = 'color__swatch-tooltip';
        tip.textContent = c.value;
        btn.appendChild(tip);
        btn.addEventListener('click', function () {
          if (selectedColor === c.value) return;
          selectedColor = c.value;
          renderSwatches();
          renderVariants();
        });
        swatchEl.appendChild(btn);
      });
      if (colorCurrentEl) {
        colorCurrentEl.textContent = selectedColor ? selectedColor : '';
      }
    }

    /**
     * 右侧选购区：按商品分组、变体卡片、卡片上携带拖拽 JSON。
     * 整张卡片可拖；内部 img 设 draggable=false，拖拽预览用图片或图形容器。
     */
    function renderVariants() {
      variantsEl.classList.remove('office-layout-configurator__variants--grid');
      if (!getColorPalette().length) {
        variantsEl.innerHTML = '';
        return;
      }
      if (!selectedColor) {
        variantsEl.innerHTML =
          '<p class="office-layout-configurator__empty-variants">No variants for this color.</p>';
        return;
      }
      var rows = filteredVariants(selectedColor);
      if (!rows.length) {
        variantsEl.innerHTML =
          '<p class="office-layout-configurator__empty-variants">No variants for this color.</p>';
        return;
      }
      variantsEl.innerHTML = '';
      variantsEl.classList.add('office-layout-configurator__variants--grid');
      var groups = groupRowsByProduct(rows);
      groups.forEach(function (g) {
        var wrap = document.createElement('div');
        wrap.className = 'office-layout-configurator__variants-sku-group';
        var firstRow = g.rows[0];
        var showHeading = shouldShowSkuSectionHeading(firstRow, products.length);
        var headingText = resolveSkuSectionHeading(firstRow);
        if (showHeading && headingText) {
          var hEl = document.createElement('h3');
          hEl.className = 'office-layout-configurator__variants-sku-heading';
          var baseSpan = document.createElement('span');
          baseSpan.className = 'office-layout-configurator__variants-sku-heading__base';
          baseSpan.textContent = formatSkuSectionHeading(headingText);
          var curSpan = document.createElement('span');
          curSpan.className = 'office-layout-configurator__variants-sku-heading__current';
          curSpan.setAttribute('data-olc-sku-heading-current', '');
          curSpan.setAttribute('data-product-id', String(firstRow.productId));
          curSpan.setAttribute('aria-live', 'polite');
          hEl.appendChild(baseSpan);
          hEl.appendChild(curSpan);
          curSpan.textContent = variantPreviewLabel(firstRow.variant);
          wrap.appendChild(hEl);
        }
        var grid = document.createElement('div');
        grid.className = 'office-layout-configurator__variants-grid';
        var hideProductLineOnCard = !!(showHeading && headingText);
        g.rows.forEach(function (row) {
          var v = row.variant;
          var card = document.createElement('div');
          card.className = 'office-layout-configurator__variant-card';
          if (!v.available) {
            card.classList.add('office-layout-configurator__variant-card--unavailable');
          }
          var pRow = findProductById(row.productId);
          var displayImg = resolveVariantImageUrl(v, pRow);
          var skuTitle =
            v.skuTitle != null && String(v.skuTitle).trim() !== ''
              ? String(v.skuTitle).trim()
              : v.title != null && String(v.title).trim() !== ''
                ? String(v.title).trim()
                : variantLabel(v, colorIdx);
          var payloadObj = {
            variantId: v.id,
            productId: row.productId,
            image: displayImg,
            title: variantLabel(v, colorIdx),
            skuTitle: skuTitle,
            price: v.price,
            productTitle: row.productTitle,
            sideCabinetOnly: !!row.sideCabinetOnly,
          };
          var payloadJson = JSON.stringify(payloadObj);
          card.dataset.variantPayload = payloadJson;
          card.draggable = !!v.available;
          var imgWrap = document.createElement('div');
          imgWrap.className = 'office-layout-configurator__variant-card__image';
          var img = document.createElement('img');
          img.src = displayImg || '';
          img.alt = '';
          img.loading = 'lazy';
          img.draggable = false;
          imgWrap.appendChild(img);
          if (!v.available) {
            appendOutOfStockBadge(imgWrap);
          }
          var body = document.createElement('div');
          body.className = 'office-layout-configurator__variant-card__body';
          if (row.productTitle && !hideProductLineOnCard) {
            var pt = document.createElement('p');
            pt.className = 'office-layout-configurator__variant-card__product';
            pt.textContent = row.productTitle;
            body.appendChild(pt);
          }
          var dim = document.createElement('p');
          dim.className = 'office-layout-configurator__variant-card__dim';
          dim.textContent = variantLabel(v, colorIdx) || row.productTitle;
          var pr = document.createElement('p');
          pr.className = 'office-layout-configurator__variant-card__price';
          pr.textContent = formatMoney(v.price, moneyFormat);
          body.appendChild(dim);
          body.appendChild(pr);
          card.appendChild(imgWrap);
          card.appendChild(body);

          var suppressNextClick = false;
          card.addEventListener('dragstart', function (e) {
            if (!v.available) return;
            e.dataTransfer.setData(MIME, card.dataset.variantPayload);
            try {
              e.dataTransfer.setData('text/plain', card.dataset.variantPayload);
            } catch (err) {}
            e.dataTransfer.effectAllowed = 'copy';
            // 拖拽预览：视口外克隆全图，避免滚动裁切时幽灵图混入主图外像素。
            try {
              setOlcVariantDragImageFromClone(img, e, imgWrap);
            } catch (setImgErr) {}
          });
          card.addEventListener('dragend', function () {
            suppressNextClick = true;
            setTimeout(function () {
              suppressNextClick = false;
            }, 50);
          });
          card.addEventListener('click', function () {
            if (!v.available || suppressNextClick) return;
            if (showHeading && headingText) {
              setSkuHeadingPreview(row.productId, v);
            }
          });

          grid.appendChild(card);
        });
        wrap.appendChild(grid);
        variantsEl.appendChild(wrap);
      });
    }

    /** 任意第一个空槽索引；非边柜自动落位时使用。 */
    function firstEmptyIndex() {
      for (var i = 0; i < slots.length; i++) {
        if (!slots[i]) return i;
      }
      return -1;
    }

    /**
     * 左紧凑且右缘槽仍空：如 [边柜,立柜,null,null,null] 或 [立柜,null,…]。
     * 画布「右侧加号」须绑右缘槽（maxSlots-1），否则 firstEmpty 落在中间索引，
     * 边柜会因 !isEdgeSlot 被拒（maxSlots=3 时中间刚好等于右缘，问题被掩盖）。
     */
    function isLeftCompactedWithRightEdgeEmpty() {
      if (maxSlots < 3) return false;
      if (slots[maxSlots - 1]) return false;
      return slots.some(Boolean);
    }

    /** 画布上唯一空槽加号使用的索引（与 firstEmptyIndex 可能不同）。 */
    function firstEmptySlotIndexForCanvasUI() {
      var fe = firstEmptyIndex();
      if (fe < 0) return fe;
      if (isLeftCompactedWithRightEdgeEmpty()) {
        return maxSlots - 1;
      }
      return fe;
    }

    function notifyUnavailableVariant() {
      var msg =
        (config.strings && config.strings.unavailableVariant) ||
        'This variant is out of stock.';
      if (window.BMessage && typeof window.BMessage.warning === 'function') {
        window.BMessage.warning({ content: msg });
      } else if (window.BMessage && typeof window.BMessage.error === 'function') {
        window.BMessage.error({ content: msg });
      } else {
        alert(msg);
      }
    }

    function getOutOfStockBadgeText() {
      var text = config.strings && config.strings.outOfStockBadge;
      if (text == null) return 'Out of stock';
      text = String(text).trim();
      return text || 'Out of stock';
    }

    function appendOutOfStockBadge(container) {
      var badgeText = getOutOfStockBadgeText();
      if (!badgeText) return;
      var badge = document.createElement('span');
      badge.className = 'office-layout-configurator__unavailable-badge';
      badge.textContent = badgeText;
      container.appendChild(badge);
    }

    /** 弹窗内可选的首个变体：有库存且（边柜时须在两侧槽） */
    function firstPlaceableModalRow(rows, edgeSlotIndex) {
      for (var i = 0; i < rows.length; i++) {
        var r = rows[i];
        if (!r.variant.available) continue;
        var blk =
          !!r.sideCabinetOnly &&
          edgeSlotIndex !== null &&
          edgeSlotIndex !== undefined &&
          !isSideCabinetAllowedSlot(edgeSlotIndex);
        if (!blk) return r;
      }
      return null;
    }

    function findRowByVariantId(vid) {
      var out = null;
      flatRows.forEach(function (row) {
        if (String(row.variant.id) === String(vid)) out = row;
      });
      return out;
    }

    /**
     * 画布 .office-layout-configurator__slot-name：与右侧 .office-layout-configurator__variants-sku-heading__base
     * 同源（formatSkuSectionHeading(resolveSkuSectionHeading(row))），再去除冒号；无法解析时回退商品/变体标题。
     */
    function slotFooterSkuGroupTitle(slot) {
      if (!slot) return '';
      var row = findRowByVariantId(slot.variantId);
      if (row) {
        var baseText = formatSkuSectionHeading(resolveSkuSectionHeading(row));
        var fromHeading = stripColonsForSkuHeadingDisplay(baseText);
        if (fromHeading) return fromHeading;
      }
      var raw =
        (slot.productTitle && String(slot.productTitle).trim()) ||
        (slot.skuTitle && String(slot.skuTitle).trim()) ||
        (slot.variantTitle && String(slot.variantTitle).trim()) ||
        (slot.title && String(slot.title).trim()) ||
        '';
      return stripColonsForSkuHeadingDisplay(raw);
    }

    function findRowBySku(sku) {
      var norm = String(sku || '').trim();
      if (!norm) return null;
      var out = null;
      flatRows.forEach(function (row) {
        var s = row.variant.sku != null ? String(row.variant.sku).trim() : '';
        if (s && s === norm) out = row;
      });
      return out;
    }

    /** 预设列表项：纯数字按 Variant ID 解析，否则按 SKU；ID 未命中时再按 SKU 尝试。 */
    function findRowByPresetToken(token) {
      var norm = String(token || '').trim();
      if (!norm) return null;
      if (/^\d+$/.test(norm)) {
        var byId = findRowByVariantId(norm);
        if (byId) return byId;
      }
      return findRowBySku(norm);
    }

    function notifyPresetError(msg) {
      var text = msg || 'Unable to apply configuration.';
      if (window.BMessage && typeof window.BMessage.warning === 'function') {
        window.BMessage.warning({ content: text });
      } else {
        alert(text);
      }
    }

    function variantPreviewLabel(v) {
      if (!v) return '';
      var name = variantLabel(v, colorIdx);
      if (name && String(name).trim()) return String(name).trim();
      if (v.skuTitle != null && String(v.skuTitle).trim()) return String(v.skuTitle).trim();
      return '';
    }

    function setSkuHeadingPreview(productId, v) {
      if (!variantsEl) return;
      var name = variantPreviewLabel(v);
      variantsEl.querySelectorAll('[data-olc-sku-heading-current]').forEach(function (el) {
        if (String(el.getAttribute('data-product-id')) === String(productId)) {
          el.textContent = name;
        }
      });
    }

    function setModalSectionHeadingPreview(productId, v) {
      if (!modalVariantsEl) return;
      var name = variantPreviewLabel(v);
      modalVariantsEl.querySelectorAll('[data-olc-modal-section-heading-current]').forEach(function (el) {
        if (String(el.getAttribute('data-product-id')) === String(productId)) {
          el.textContent = name;
        }
      });
    }

    function setModalTitlePreviewName(v) {
      if (!modalTitleCurrentEl) return;
      var name = variantPreviewLabel(v);
      modalTitleCurrentEl.textContent = name || '';
    }

    function setModalTitlePreviewByVariantId(vid) {
      var row = findRowByVariantId(vid);
      if (row && row.variant) {
        setModalTitlePreviewName(row.variant);
        return;
      }
      var p = findProductById(modalProductId);
      if (p && p.variants) {
        for (var i = 0; i < p.variants.length; i++) {
          if (String(p.variants[i].id) === String(vid)) {
            setModalTitlePreviewName(p.variants[i]);
            return;
          }
        }
      }
      if (modalTitleCurrentEl) modalTitleCurrentEl.textContent = '';
    }

    /** 按 id 从 config.products 中取商品对象。 */
    function findProductById(pid) {
      for (var i = 0; i < products.length; i++) {
        if (String(products[i].id) === String(pid)) return products[i];
      }
      return null;
    }

    /**
     * 从 product.images 中取「该 SKU 所属系列」主图：先找到 variant_ids 含该 variant 的图，
     * 再沿 position 向后连续取 variant_ids 为空的图，最后一张的 src 为主图。
     */
    function lastImageSrcInVariantSeries(images, variantId) {
      if (!images || !images.length || variantId == null) return '';
      var vid = String(variantId);
      var sorted = images.slice().sort(function (a, b) {
        return (a.position || 0) - (b.position || 0);
      });
      var startIdx = -1;
      for (var i = 0; i < sorted.length; i++) {
        var ids = sorted[i].variant_ids || [];
        var has = false;
        for (var k = 0; k < ids.length; k++) {
          if (String(ids[k]) === vid) {
            has = true;
            break;
          }
        }
        if (has) {
          startIdx = i;
          break;
        }
      }
      if (startIdx < 0) return '';
      var lastIdx = startIdx;
      for (var j = startIdx + 1; j < sorted.length; j++) {
        var ids2 = sorted[j].variant_ids || [];
        if (!ids2 || ids2.length === 0) {
          lastIdx = j;
        } else {
          break;
        }
      }
      var last = sorted[lastIdx];
      return last && last.src ? last.src : '';
    }

    function resolveVariantImageUrl(variant, product) {
      var fallback = variant && variant.image ? variant.image : '';
      if (!variant || !product || !product.images || !product.images.length) return fallback;
      var resolved = lastImageSrcInVariantSeries(product.images, variant.id);
      return resolved || fallback;
    }

    function rowPayloadFromRow(row) {
      var v = row.variant;
      var p = findProductById(row.productId);
      var skuTitle =
        v.skuTitle != null && String(v.skuTitle).trim() !== ''
          ? String(v.skuTitle).trim()
          : v.title != null && String(v.title).trim() !== ''
            ? String(v.title).trim()
            : variantLabel(v, colorIdx);
      return {
        variantId: v.id,
        productId: row.productId,
        image: resolveVariantImageUrl(v, p),
        title: variantLabel(v, colorIdx),
        skuTitle: skuTitle,
        price: v.price,
        productTitle: row.productTitle,
        sideCabinetOnly: !!row.sideCabinetOnly,
      };
    }

    /** 删除/重排后，将 placementSeq 重编为 1…n，供底部徽章显示。 */
    function compactPlacementSeq() {
      var filled = [];
      for (var i = 0; i < slots.length; i++) {
        if (slots[i]) {
          filled.push({ slot: slots[i], index: i });
        }
      }
      filled.sort(function (a, b) {
        return (a.slot.placementSeq || 0) - (b.slot.placementSeq || 0);
      });
      filled.forEach(function (x, i) {
        x.slot.placementSeq = i + 1;
      });
      nextPlacementSeq = filled.length + 1;
    }

    function slotBadgeNumber(index, slot) {
      if (!slot) return '';
      var filled = slots.filter(Boolean).length;
      if (filled === maxSlots && maxSlots > 0) {
        return String(index + 1);
      }
      return String(slot.placementSeq || index + 1);
    }

    /** 总宽线像素宽度与首末已填槽（SKU 图区域）横向跨度一致。 */
    function positionOlcDimensionTotalLine() {
      if (!dimensionTotalEl || dimensionTotalEl.hidden) return;
      var line = dimensionTotalEl.querySelector('[data-olc-dimension-total-line]');
      if (!line || !slotsRoot) return;
      var filled = slotsRoot.querySelectorAll('.office-layout-configurator__slot.is-filled');
      if (!filled.length) return;
      var first = filled[0];
      var last = filled[filled.length - 1];
      var dRect = dimensionTotalEl.getBoundingClientRect();
      var a = first.getBoundingClientRect();
      var b = last.getBoundingClientRect();
      var left = Math.round(a.left - dRect.left);
      var width = Math.round(b.right - a.left);
      line.style.position = 'absolute';
      line.style.top = '0';
      line.style.left = left + 'px';
      line.style.width = Math.max(0, width) + 'px';
      line.style.boxSizing = 'border-box';
    }

    function schedulePositionOlcDimensionTotalLine() {
      requestAnimationFrame(function () {
        positionOlcDimensionTotalLine();
      });
    }

    /** 画布上方总宽：无 dim-segments；dim-total-line 内含 linebox + 数字，宽度为已选槽位图区像素宽度之和（外缘包络）。 */
    function renderDimensionBar() {
      if (!dimensionTotalEl) return;
      dimensionTotalEl.innerHTML = '';
      dimensionTotalEl.style.position = '';
      dimensionTotalEl.style.minHeight = '';
      var filledSlots = slots.filter(Boolean);
      if (filledSlots.length < 2 || !canvasExpanded()) {
        dimensionTotalEl.hidden = true;
        return;
      }
      var sum = filledSlots.reduce(function (acc, s) {
        return acc + slotWidthNumericForSum(s);
      }, 0);
      var totalLabel = sum % 1 === 0 ? String(sum) : sum.toFixed(1);
      dimensionTotalEl.style.position = 'relative';
      dimensionTotalEl.style.minHeight = '28px';
      var line = document.createElement('div');
      line.className = 'office-layout-configurator__dim-total-line';
      line.setAttribute('data-olc-dimension-total-line', '');
      var linebox = document.createElement('div');
      linebox.className = 'linebox';
      var numEl = document.createElement('span');
      numEl.className = 'office-layout-configurator__dim-total-num';
      numEl.textContent = totalLabel;
      line.appendChild(linebox);
      line.appendChild(numEl);
      dimensionTotalEl.appendChild(line);
      dimensionTotalEl.hidden = false;
      schedulePositionOlcDimensionTotalLine();
    }

    if (typeof ResizeObserver !== 'undefined' && slotsRoot) {
      if (root._olcDimTotalRO) {
        root._olcDimTotalRO.disconnect();
      }
      root._olcDimTotalRO = new ResizeObserver(function () {
        if (!dimensionTotalEl || dimensionTotalEl.hidden) return;
        positionOlcDimensionTotalLine();
      });
      root._olcDimTotalRO.observe(slotsRoot);
      if (dimensionTotalEl) root._olcDimTotalRO.observe(dimensionTotalEl);
      if (canvasEl) root._olcDimTotalRO.observe(canvasEl);
    }

    /** 无法再放置时：弹层（锁滚动）或 BMessage / alert。 */
    function showMaxSlotsPopover() {
      if (maxPopoverEl) {
        if (!maxPopoverEl.hidden) return;
        maxPopoverEl.hidden = false;
        lockOlcBodyScroll();
      } else if (window.BMessage && window.BMessage.warning) {
        window.BMessage.warning({
          content:
            (config.strings && config.strings.maxSlotsReachedBody) ||
            'Maximum number of items reached.',
        });
      } else {
        alert((config.strings && config.strings.maxSlotsReachedBody) || 'Maximum reached.');
      }
    }

    function hideMaxSlotsPopover() {
      if (!maxPopoverEl || maxPopoverEl.hidden) return;
      maxPopoverEl.hidden = true;
      unlockOlcBodyScroll();
    }

    /** 关闭选款/编辑弹窗并清空弹窗专用状态。 */
    function closeEditModal() {
      if (!editModalEl) return;
      if (editModalEl.hidden) return;
      editModalEl.hidden = true;
      editModalEl.setAttribute('aria-hidden', 'true');
      unlockOlcBodyScroll();
      modalSlotIndex = null;
      modalProductId = null;
      modalSelectedVariantId = null;
      modalPickSlotIndex = null;
      if (modalTitleCurrentEl) modalTitleCurrentEl.textContent = '';
    }

    /** 选槽 / 编辑弹窗：左侧插入槽位按索引 0 判断边柜是否可放。 */
    function getModalEdgeSlotIndexForSideBlock() {
      if (modalMode === 'pickSlot' && modalPickSlotIndex === 'prepend') return 0;
      if (modalMode === 'pickSlot') return modalPickSlotIndex;
      return modalSlotIndex;
    }

    /** 选槽 / 编辑弹窗共用：无库存置灰；边柜 + 中间槽时不可选并提示 */
    function appendModalVariantCards(grid, rows, showProductTitle) {
      if (showProductTitle === undefined) showProductTitle = true;
      var edgeSlotForBlock = getModalEdgeSlotIndexForSideBlock();
      rows.forEach(function (row) {
        var v = row.variant;
        var pRow = findProductById(row.productId);
        var displayImg = resolveVariantImageUrl(v, pRow);
        var unavailable = !v.available;
        var blockedSide =
          !!row.sideCabinetOnly &&
          edgeSlotForBlock !== null &&
          edgeSlotForBlock !== undefined &&
          !isSideCabinetAllowedSlot(edgeSlotForBlock);
        var card = document.createElement('button');
        card.type = 'button';
        card.className =
          'office-layout-configurator__modal-variant-card' +
          (String(v.id) === String(modalSelectedVariantId) ? ' is-selected' : '') +
          (unavailable ? ' is-unavailable' : '') +
          (blockedSide ? ' is-slot-blocked' : '');
        card.dataset.variantId = String(v.id);
        if (unavailable) card.disabled = true;
        var imgWrap = document.createElement('div');
        imgWrap.className = 'office-layout-configurator__modal-variant-card__image';
        var img = document.createElement('img');
        img.src = displayImg || '';
        img.alt = '';
        imgWrap.appendChild(img);
        if (unavailable) {
          appendOutOfStockBadge(imgWrap);
        }
        card.appendChild(imgWrap);
        if (showProductTitle && row.productTitle) {
          var pt = document.createElement('span');
          pt.className = 'office-layout-configurator__modal-variant-card__product';
          pt.textContent = row.productTitle;
          card.appendChild(pt);
        }
        var dim = document.createElement('span');
        dim.className = 'office-layout-configurator__modal-variant-card__dim';
        dim.textContent = variantLabel(v, colorIdx);
        var pr = document.createElement('span');
        pr.className = 'office-layout-configurator__modal-variant-card__price';
        pr.textContent = formatMoney(v.price, moneyFormat);
        card.appendChild(dim);
        card.appendChild(pr);
        card.addEventListener('click', function () {
          if (unavailable) return;
          if (blockedSide) {
            notifySideCabinetEdgeOnly();
            return;
          }
          modalSelectedVariantId = v.id;
          setModalSectionHeadingPreview(row.productId, v);
          if (modalMode === 'edit') {
            setModalTitlePreviewName(v);
          }
          modalVariantsEl.querySelectorAll('.office-layout-configurator__modal-variant-card').forEach(function (el) {
            el.classList.toggle('is-selected', el.dataset.variantId === String(v.id));
          });
        });
        grid.appendChild(card);
      });
    }

    function renderModalVariantGridAll(modalColorValue) {
      if (!modalVariantsEl) return;
      var rows = filteredVariants(modalColorValue);
      modalVariantsEl.innerHTML = '';
      if (!rows.length) return;
      var groups = groupRowsByProduct(rows);
      groups.forEach(function (g) {
        var firstRow = g.rows[0];
        var showHeading = shouldShowSkuSectionHeading(firstRow, products.length);
        var headingText = resolveSkuSectionHeading(firstRow);
        var section = document.createElement('div');
        section.className = 'office-layout-configurator__modal-variants-section';
        if (showHeading && headingText) {
          var ht = document.createElement('h4');
          ht.className = 'office-layout-configurator__modal-variants-section-title';
          var mBase = document.createElement('span');
          mBase.className = 'office-layout-configurator__modal-variants-section-title__base';
          mBase.textContent = formatSkuSectionHeading(headingText);
          var mCur = document.createElement('span');
          mCur.className = 'office-layout-configurator__modal-variants-section-title__current';
          mCur.setAttribute('data-olc-modal-section-heading-current', '');
          mCur.setAttribute('data-product-id', String(firstRow.productId));
          mCur.setAttribute('aria-live', 'polite');
          ht.appendChild(mBase);
          ht.appendChild(mCur);
          section.appendChild(ht);
        }
        var grid = document.createElement('div');
        grid.className = 'office-layout-configurator__modal-variants-grid';
        appendModalVariantCards(grid, g.rows, !(showHeading && headingText));
        section.appendChild(grid);
        modalVariantsEl.appendChild(section);
      });
      if (rows.length) {
        var selOk = rows.some(function (r) {
          return String(r.variant.id) === String(modalSelectedVariantId);
        });
        if (!selOk) modalSelectedVariantId = null;
        if (!modalSelectedVariantId) {
          var pickRow = firstPlaceableModalRow(rows, getModalEdgeSlotIndexForSideBlock());
          modalSelectedVariantId = pickRow ? pickRow.variant.id : rows[0].variant.id;
        } else {
          var curPick = findRowByVariantId(modalSelectedVariantId);
          var edgeForPick = getModalEdgeSlotIndexForSideBlock();
          if (
            curPick &&
            (!curPick.variant.available ||
              (!!curPick.sideCabinetOnly &&
                edgeForPick !== null &&
                edgeForPick !== undefined &&
                !isSideCabinetAllowedSlot(edgeForPick)))
          ) {
            var pickRow2 = firstPlaceableModalRow(rows, edgeForPick);
            modalSelectedVariantId = pickRow2 ? pickRow2.variant.id : rows[0].variant.id;
          }
        }
        modalVariantsEl.querySelectorAll('.office-layout-configurator__modal-variant-card').forEach(function (el) {
          el.classList.toggle('is-selected', el.dataset.variantId === String(modalSelectedVariantId));
        });
      }
      /* 选槽模式：分组标题旁不默认展示 SKU，仅点击卡片后写入对应分组 */
      if (modalTitleCurrentEl) modalTitleCurrentEl.textContent = '';
    }

    function renderEditModalVariantGrid(modalColorValue) {
      if (!modalVariantsEl) return;
      var rows = filteredVariants(modalColorValue);
      modalVariantsEl.innerHTML = '';
      if (!rows.length) return;
      var groups = groupRowsByProduct(rows);
      groups.forEach(function (g) {
        var firstRow = g.rows[0];
        var showHeading = shouldShowSkuSectionHeading(firstRow, products.length);
        var headingText = resolveSkuSectionHeading(firstRow);
        var section = document.createElement('div');
        section.className = 'office-layout-configurator__modal-variants-section';
        if (showHeading && headingText) {
          var ht = document.createElement('h4');
          ht.className = 'office-layout-configurator__modal-variants-section-title';
          var mBase = document.createElement('span');
          mBase.className = 'office-layout-configurator__modal-variants-section-title__base';
          mBase.textContent = formatSkuSectionHeading(headingText);
          var mCur = document.createElement('span');
          mCur.className = 'office-layout-configurator__modal-variants-section-title__current';
          mCur.setAttribute('data-olc-modal-section-heading-current', '');
          mCur.setAttribute('data-product-id', String(firstRow.productId));
          mCur.setAttribute('aria-live', 'polite');
          ht.appendChild(mBase);
          ht.appendChild(mCur);
          section.appendChild(ht);
          var vCurSel = null;
          for (var ri = 0; ri < g.rows.length; ri++) {
            if (String(g.rows[ri].variant.id) === String(modalSelectedVariantId)) {
              vCurSel = g.rows[ri].variant;
              break;
            }
          }
          if (!vCurSel) vCurSel = g.rows[0].variant;
          mCur.textContent = vCurSel ? variantPreviewLabel(vCurSel) : '';
        }
        var grid = document.createElement('div');
        grid.className = 'office-layout-configurator__modal-variants-grid';
        appendModalVariantCards(grid, g.rows, !(showHeading && headingText));
        section.appendChild(grid);
        modalVariantsEl.appendChild(section);
      });
      if (rows.length) {
        var selOk = rows.some(function (r) {
          return String(r.variant.id) === String(modalSelectedVariantId);
        });
        if (!selOk) modalSelectedVariantId = null;
        if (!modalSelectedVariantId) {
          var pickRow = firstPlaceableModalRow(rows, modalSlotIndex);
          modalSelectedVariantId = pickRow ? pickRow.variant.id : rows[0].variant.id;
        } else {
          var curPick = findRowByVariantId(modalSelectedVariantId);
          if (
            curPick &&
            (!curPick.variant.available ||
              (!!curPick.sideCabinetOnly &&
                modalSlotIndex !== null &&
                modalSlotIndex !== undefined &&
                !isSideCabinetAllowedSlot(modalSlotIndex)))
          ) {
            var pickRow2 = firstPlaceableModalRow(rows, modalSlotIndex);
            modalSelectedVariantId = pickRow2 ? pickRow2.variant.id : rows[0].variant.id;
          }
        }
        modalVariantsEl.querySelectorAll('.office-layout-configurator__modal-variant-card').forEach(function (el) {
          el.classList.toggle('is-selected', el.dataset.variantId === String(modalSelectedVariantId));
        });
      }
      setModalTitlePreviewByVariantId(modalSelectedVariantId);
    }

    function renderModalVariantGrid(modalColorValue) {
      if (modalMode === 'pickSlot') {
        renderModalVariantGridAll(modalColorValue);
        return;
      }
      if (modalMode === 'edit') {
        renderEditModalVariantGrid(modalColorValue);
      }
    }

    function syncModalSwatches() {
      if (!modalSwatchesEl) return;
      var palette = getColorPalette();
      modalSwatchesEl.innerHTML = '';
      if (!palette.length) return;
      if (!modalColor || !palette.some(function (c) {
        return c.value === modalColor;
      })) {
        modalColor = palette[0].value;
      }
      palette.forEach(function (c) {
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.className =
          'office-layout-configurator__color-swatch color__swatch office-layout-configurator__modal-swatch' +
          (c.value === modalColor ? ' is-active' : '');
        btn.setAttribute('aria-label', c.value);
        btn.setAttribute('data-color', c.value);
        setSwatchCssVars(btn, c.swatchUrl, c.fallbackHandle);
        btn.addEventListener('click', function () {
          if (modalColor === c.value) return;
          modalColor = c.value;
          if (modalColorCurrentEl) modalColorCurrentEl.textContent = modalColor ? modalColor : '';
          modalSwatchesEl.querySelectorAll('.office-layout-configurator__modal-swatch').forEach(function (b) {
            b.classList.toggle('is-active', b.getAttribute('data-color') === modalColor);
          });
          renderModalVariantGrid(modalColor);
        });
        modalSwatchesEl.appendChild(btn);
      });
      if (modalColorCurrentEl) {
        modalColorCurrentEl.textContent = modalColor ? modalColor : '';
      }
    }

    /** 编辑已填充槽位：弹窗内换色 + 选 SKU。 */
    function openEditModal(slotIndex) {
      var slot = slots[slotIndex];
      if (!slot || !editModalEl) return;
      modalMode = 'edit';
      modalSlotIndex = slotIndex;
      modalProductId = slot.productId;
      modalSelectedVariantId = slot.variantId;
      var p0 = findProductById(slot.productId);
      var v0 = null;
      if (p0 && p0.variants) {
        for (var vi = 0; vi < p0.variants.length; vi++) {
          if (String(p0.variants[vi].id) === String(slot.variantId)) {
            v0 = p0.variants[vi];
            break;
          }
        }
      }
      modalColor = optionValueAt(v0 || {}, colorIdx);
      if (!modalColor) modalColor = selectedColor;
      if (modalTitleBaseEl) {
        modalTitleBaseEl.textContent =
          (config.strings && config.strings.editModalTitle) || 'Edit the color of your design';
      } else if (modalHeadingEl) {
        modalHeadingEl.textContent =
          (config.strings && config.strings.editModalTitle) || 'Edit the color of your design';
      }
      if (modalTitleCurrentEl) modalTitleCurrentEl.textContent = '';
      if (modalProductLineEl) {
        var p = findProductById(slot.productId);
        var v = null;
        if (p && p.variants) {
          for (var vj = 0; vj < p.variants.length; vj++) {
            if (String(p.variants[vj].id) === String(slot.variantId)) {
              v = p.variants[vj];
              break;
            }
          }
        }
        var dim = v ? variantLabel(v, colorIdx) : slot.variantTitle || '';
        modalProductLineEl.textContent = p ? p.title + (dim ? ': ' + dim : '') : '';
      }
      syncModalSwatches();
      renderModalVariantGrid(modalColor);
      var modalWasHidden = editModalEl.hidden;
      editModalEl.hidden = false;
      editModalEl.setAttribute('aria-hidden', 'false');
      if (modalWasHidden) lockOlcBodyScroll();
    }

    /** 点击空槽：按槽位索引选 SKU；收起态占位则传 null 由内部自动选槽。 */
    function openModalPickSlot(slotIndex) {
      if (!editModalEl) return;
      modalMode = 'pickSlot';
      modalPickSlotIndex = slotIndex;
      modalSlotIndex = null;
      modalProductId = products.length ? products[0].id : null;
      modalSelectedVariantId = null;
      modalColor = selectedColor;
      if (modalTitleBaseEl) {
        modalTitleBaseEl.textContent =
          (config.strings && config.strings.editModalTitle) || 'Edit the color of your design';
      } else if (modalHeadingEl) {
        modalHeadingEl.textContent =
          (config.strings && config.strings.editModalTitle) || 'Edit the color of your design';
      }
      if (modalTitleCurrentEl) modalTitleCurrentEl.textContent = '';
      if (modalProductLineEl) modalProductLineEl.textContent = '';
      syncModalSwatches();
      renderModalVariantGrid(modalColor);
      var pickModalWasHidden = editModalEl.hidden;
      editModalEl.hidden = false;
      editModalEl.setAttribute('aria-hidden', 'false');
      if (pickModalWasHidden) lockOlcBodyScroll();
    }

    /** 弹窗确认：选槽模式落子，或编辑模式替换当前槽位变体。 */
    function applyModalSelection() {
      if (modalMode === 'pickSlot') {
        if (!modalSelectedVariantId) {
          closeEditModal();
          return;
        }
        var rowPick = findRowByVariantId(modalSelectedVariantId);
        if (!rowPick || !rowPick.variant) {
          closeEditModal();
          return;
        }
        if (!rowPick.variant.available) {
          notifyUnavailableVariant();
          return;
        }
        if (modalPickSlotIndex === 'prepend') {
          placeVariantPrepend(rowPayloadFromRow(rowPick));
          closeEditModal();
          return;
        }
        if (
          rowPick.sideCabinetOnly &&
          modalPickSlotIndex !== null &&
          modalPickSlotIndex !== undefined &&
          !isSideCabinetAllowedSlot(modalPickSlotIndex)
        ) {
          notifySideCabinetEdgeOnly();
          return;
        }
        var payloadPick = rowPayloadFromRow(rowPick);
        if (modalPickSlotIndex === null || modalPickSlotIndex === undefined) {
          placeVariant(payloadPick, null);
        } else {
          placeVariant(payloadPick, modalPickSlotIndex);
        }
        closeEditModal();
        return;
      }
      if (!modalSelectedVariantId) {
        closeEditModal();
        return;
      }
      var row = findRowByVariantId(modalSelectedVariantId);
      if (!row || !row.variant) {
        closeEditModal();
        return;
      }
      var v = row.variant;
      if (!v.available) {
        notifyUnavailableVariant();
        return;
      }
      if (
        row.sideCabinetOnly &&
        modalSlotIndex !== null &&
        modalSlotIndex !== undefined &&
        !isSideCabinetAllowedSlot(modalSlotIndex)
      ) {
        notifySideCabinetEdgeOnly();
        return;
      }
      var payload = rowPayloadFromRow(row);
      if (modalMode === 'edit' && modalSlotIndex !== null && modalSlotIndex >= 0) {
        var side = !!payload.sideCabinetOnly;
        if (side && !isSideCabinetAllowedSlot(modalSlotIndex)) {
          notifySideCabinetEdgeOnly();
          return;
        }
        var prevSlot = slots[modalSlotIndex];
        slots[modalSlotIndex] = {
          variantId: payload.variantId,
          productId: payload.productId,
          image: payload.image,
          title: payload.title,
          variantTitle: payload.title,
          price: payload.price,
          productTitle: payload.productTitle,
          skuTitle: payload.skuTitle || '',
          widthLabel: widthLabelFromVariantTitle(payload.title),
          sideCabinetOnly: !!payload.sideCabinetOnly,
          placementSeq: prevSlot && prevSlot.placementSeq != null ? prevSlot.placementSeq : nextPlacementSeq++,
        };
        closeEditModal();
        renderSlotCanvas();
        renderVariants();
        updatePrice();
        return;
      }
      closeEditModal();
    }

    if (editModalEl) {
      editModalEl.querySelectorAll('[data-olc-modal-dismiss]').forEach(function (el) {
        el.addEventListener('click', closeEditModal);
      });
      if (modalConfirmBtn) {
        modalConfirmBtn.addEventListener('click', applyModalSelection);
      }
    }
    if (maxPopoverEl) {
      maxPopoverEl.querySelectorAll('[data-olc-max-popover-dismiss]').forEach(function (el) {
        el.addEventListener('click', hideMaxSlotsPopover);
      });
    }

    var mobileCanvasLayoutTimer = null;

    function isMobileOlcViewport() {
      return window.matchMedia('(max-width: 749px)').matches;
    }

    function clearMobileCanvasFitScale() {
      if (!canvasStageEl) return;
      canvasStageEl.classList.remove('is-fit-scaled');
      canvasStageEl.style.removeProperty('--olc-fit-scale');
      canvasStageEl.style.marginBottom = '';
    }

    function clearMobileSlotVisualWidths() {
      if (!slotsRoot) return;
      slotsRoot
        .querySelectorAll('.office-layout-configurator__slot-inner, .office-layout-configurator__slot-visual')
        .forEach(function (node) {
          node.style.width = '';
        });
      slotsRoot.querySelectorAll('.office-layout-configurator__slot-img').forEach(function (img) {
        img.style.width = '';
        img.style.height = '';
        img.style.maxHeight = '';
      });
    }

    /** 按槽位行高度 × 图片固有比例估算展示宽度（避免真机把图撑成 intrinsic 宽）。 */
    function getMobileSlotRowHeight() {
      if (slotsRoot && slotsRoot.clientHeight > 0) return slotsRoot.clientHeight;
      if (canvasStageEl) {
        var wrap = canvasStageEl.querySelector('.office-layout-configurator__slots-wrap');
        if (wrap && wrap.clientHeight > 0) return wrap.clientHeight;
      }
      if (canvasEl && canvasEl.clientHeight > 0) {
        return Math.max(0, canvasEl.clientHeight - 56);
      }
      return 0;
    }

    function measureSlotImgDisplayWidth(slotEl, img) {
      if (!slotEl || !img) return 0;
      var nw = img.naturalWidth || 0;
      var nh = img.naturalHeight || 0;
      var h = getMobileSlotRowHeight();
      if (nw > 0 && nh > 0 && h > 0) {
        return h * (nw / nh);
      }
      return 0;
    }

    function allFilledSlotImagesReady() {
      if (!slotsRoot) return false;
      var imgs = slotsRoot.querySelectorAll(
        '.office-layout-configurator__slot.is-filled .office-layout-configurator__slot-img'
      );
      if (!imgs.length) return false;
      for (var i = 0; i < imgs.length; i++) {
        if (!imgs[i].complete || !imgs[i].naturalWidth) return false;
      }
      return true;
    }

    /**
     * 移动端：用「高度 × 宽高比」写入槽位宽度，纠正 iOS 等对 height:100% 图片的 intrinsic 撑开。
     */
    function applyMobileSlotVisualWidths() {
      if (!slotsRoot) return;
      if (!isMobileOlcViewport()) {
        clearMobileSlotVisualWidths();
        return;
      }
      var rowH = getMobileSlotRowHeight();
      var filledSlots = slotsRoot.querySelectorAll('.office-layout-configurator__slot.is-filled');
      filledSlots.forEach(function (slotEl) {
        var inner = slotEl.querySelector('.office-layout-configurator__slot-inner');
        var vis = slotEl.querySelector('.office-layout-configurator__slot-visual');
        var img = slotEl.querySelector('.office-layout-configurator__slot-img');
        if (!inner || !vis || !img) return;
        var width = measureSlotImgDisplayWidth(slotEl, img);
        if (!width || width <= 0) return;
        var px = Math.ceil(width) + 'px';
        inner.style.width = px;
        vis.style.width = px;
        if (rowH > 0) {
          var hPx = Math.ceil(rowH) + 'px';
          img.style.height = hPx;
          img.style.maxHeight = hPx;
          img.style.width = px;
        }
      });
    }

    /**
     * 移动端：多柜总宽超出画布时整体等比缩小。
     * 必须先按比例校正槽位宽，且等图片 naturalWidth 就绪，否则真机会把 2 柜误缩得很小。
     */
    function applyMobileCanvasFitScale() {
      clearMobileCanvasFitScale();
      if (!canvasStageEl || !canvasEl || !slotsRoot) return;
      if (!isMobileOlcViewport()) {
        clearMobileSlotVisualWidths();
        schedulePositionOlcDimensionTotalLine();
        return;
      }
      if (isCollapsedCanvas() || !slots.some(Boolean)) {
        clearMobileSlotVisualWidths();
        schedulePositionOlcDimensionTotalLine();
        return;
      }
      if (!allFilledSlotImagesReady()) {
        schedulePositionOlcDimensionTotalLine();
        return;
      }

      applyMobileSlotVisualWidths();

      var avail = canvasEl.clientWidth;
      try {
        var cs = window.getComputedStyle(canvasEl);
        avail -= (parseFloat(cs.paddingLeft) || 0) + (parseFloat(cs.paddingRight) || 0);
      } catch (e) {}
      if (avail <= 0) return;

      var natural = 0;
      var slotEls = slotsRoot.querySelectorAll('.office-layout-configurator__slot');
      for (var si = 0; si < slotEls.length; si++) {
        natural += slotEls[si].offsetWidth || 0;
      }
      if (natural <= 0) {
        natural = Math.max(slotsRoot.scrollWidth || 0, slotsRoot.offsetWidth || 0);
      }
      if (natural <= avail + 2) {
        schedulePositionOlcDimensionTotalLine();
        return;
      }

      var scale = Math.max(0.42, Math.min(1, avail / natural));
      if (scale >= 0.995) {
        schedulePositionOlcDimensionTotalLine();
        return;
      }
      canvasStageEl.classList.add('is-fit-scaled');
      canvasStageEl.style.setProperty('--olc-fit-scale', String(scale));
      var layoutH = canvasStageEl.offsetHeight;
      if (layoutH > 0) {
        canvasStageEl.style.marginBottom = layoutH * (scale - 1) + 'px';
      }
      schedulePositionOlcDimensionTotalLine();
    }

    function scheduleMobileCanvasLayout() {
      if (mobileCanvasLayoutTimer) {
        window.clearTimeout(mobileCanvasLayoutTimer);
        mobileCanvasLayoutTimer = null;
      }
      window.requestAnimationFrame(function () {
        window.requestAnimationFrame(function () {
          applyMobileCanvasFitScale();
        });
      });
      mobileCanvasLayoutTimer = window.setTimeout(function () {
        applyMobileCanvasFitScale();
      }, 120);
    }

    /**
     * 渲染画布：无货时为单个收起占位；有货时为 maxSlots 个槽位。
     * 空槽显示 +；已填显示尺寸条 + 图片。同时刷新底部摘要与尺寸条。
     */
    function renderSlotCanvas() {
      slotsRoot.innerHTML = '';
      slotsRoot.classList.remove('office-layout-configurator__slots--dual-edge-leading');
      slotsRoot.classList.remove('office-layout-configurator__slots--edge-plus-compact');
      slotsRoot.classList.remove('office-layout-configurator__slots--edge-plus-hidden');

      var rightmostFilledIndex = -1;
      var filledSlotCount = 0;
      for (var ri = 0; ri < maxSlots; ri++) {
        if (slots[ri]) {
          filledSlotCount++;
          rightmostFilledIndex = ri;
        }
      }

      function appendEmptyPlus(el) {
        var pw = document.createElement('span');
        pw.className = 'office-layout-configurator__slot-plus-wrap';
        var pc = document.createElement('span');
        pc.className = 'office-layout-configurator__slot-plus-circle';
        pc.textContent = '+';
        pw.appendChild(pc);
        el.appendChild(pw);
      }

      function renderOneSlot(index) {
        var el = document.createElement('div');
        el.className = 'office-layout-configurator__slot';
        el.setAttribute('data-olc-slot', String(index));
        el.setAttribute('data-olc-drop-target', '');
        el.setAttribute('data-olc-slot-edge', isEdgeSlot(index) ? 'true' : 'false');
        var slot = slots[index];
        el.classList.toggle('is-filled', !!slot);
        if (!slot) {
          appendEmptyPlus(el);
        } else {
          var inner = document.createElement('div');
          inner.className = 'office-layout-configurator__slot-inner';
          var slotSkuWidth = document.createElement('div');
          slotSkuWidth.className = 'slot-sku-width';
          var linebox = document.createElement('div');
          linebox.className = 'linebox';
          var skuWidthSpan = document.createElement('span');
          skuWidthSpan.className = 'sku-width';
          skuWidthSpan.textContent = getSlotWidthDisplayText(slot);
          slotSkuWidth.appendChild(linebox);
          slotSkuWidth.appendChild(skuWidthSpan);
          inner.appendChild(slotSkuWidth);
          var vis = document.createElement('div');
          vis.className = 'office-layout-configurator__slot-visual';
          var img = document.createElement('img');
          img.className = 'office-layout-configurator__slot-img';
          img.src = slot.image;
          img.alt = '';
          img.addEventListener('load', scheduleMobileCanvasLayout, { once: true });
          if (img.complete && img.naturalWidth) {
            scheduleMobileCanvasLayout();
          }
          if (
            maxSlots > 1 &&
            slot.sideCabinetOnly &&
            index === rightmostFilledIndex &&
            (rightmostFilledIndex === maxSlots - 1 || filledSlotCount > 1)
          ) {
            img.style.transform = 'scaleX(-1)';
            img.style.transformOrigin = 'center center';
          }
          vis.appendChild(img);
          inner.appendChild(vis);
          el.appendChild(inner);
        }
        slotsRoot.appendChild(el);
      }

      function renderSlotFootersRow() {
        if (!slotFootersGridEl || !slotFootersRowEl) return;
        slotFootersGridEl.innerHTML = '';
        var hasAny = slots.some(Boolean);
        if (!hasAny) {
          slotFootersRowEl.hidden = true;
          return;
        }
        slotFootersRowEl.hidden = false;
        for (var fi = 0; fi < maxSlots; fi++) {
          var cell = document.createElement('div');
          cell.className = 'office-layout-configurator__slot-footer-cell';
          var slot = slots[fi];
          if (!slot) {
            slotFootersGridEl.appendChild(cell);
            continue;
          }
          var footer = document.createElement('div');
          footer.className = 'office-layout-configurator__slot-footer';
          var badge = document.createElement('span');
          badge.className = 'office-layout-configurator__slot-badge';
          badge.textContent = slotBadgeNumber(fi, slot);
          var nameEl = document.createElement('div');
          nameEl.className = 'office-layout-configurator__slot-name';
          nameEl.textContent = slotFooterSkuGroupTitle(slot);
          var actions = document.createElement('div');
          actions.className = 'office-layout-configurator__slot-actions';
          var editBtn = document.createElement('button');
          editBtn.type = 'button';
          editBtn.className = 'office-layout-configurator__slot-edit';
          editBtn.textContent = (config.strings && config.strings.editLabel) || 'Edit';
          (function (slotIndex) {
            editBtn.addEventListener('click', function (e) {
              e.stopPropagation();
              openEditModal(slotIndex);
            });
          })(fi);
          var rm = document.createElement('button');
          rm.type = 'button';
          rm.className = 'office-layout-configurator__slot-remove';
          rm.textContent = config.strings.remove || 'Remove';
          (function (slotIndex) {
            rm.addEventListener('click', function (e) {
              e.stopPropagation();
              slots[slotIndex] = null;
              compactSlotsLeft();
              compactPlacementSeq();
              renderSlotCanvas();
              renderVariants();
              updatePrice();
            });
          })(fi);
          actions.appendChild(editBtn);
          actions.appendChild(rm);
          footer.appendChild(badge);
          footer.appendChild(nameEl);
          footer.appendChild(actions);
          cell.appendChild(footer);
          slotFootersGridEl.appendChild(cell);
        }
      }

      if (isCollapsedCanvas()) {
        var placeholder = document.createElement('div');
        placeholder.className = 'office-layout-configurator__slot';
        placeholder.setAttribute('data-olc-slot', 'collapsed');
        placeholder.setAttribute('data-olc-drop-target', '');
        appendEmptyPlus(placeholder);
        slotsRoot.appendChild(placeholder);
      } else {
        /** 多槽展开：视觉顺序为 [左侧加号][柜子…][右侧加号]，数据仍为向左紧凑的 slots[]。 */
        var showDualEdgeUI = maxSlots > 1;
        /** 槽位上限为 5：满槽时隐藏两侧加号；剩 1 空槽时两侧加号收窄。 */
        var hideEdgePlusWhenFull = maxSlots === 5 && filledSlotCount === maxSlots;
        var compactEdgePlus = maxSlots === 5 && filledSlotCount === maxSlots - 1;
        var firstEmpty = firstEmptySlotIndexForCanvasUI();

        if (showDualEdgeUI && !hideEdgePlusWhenFull) {
          var lead = document.createElement('div');
          lead.className =
            'office-layout-configurator__slot office-layout-configurator__slot--leading-inner-prepend';
          lead.setAttribute('data-olc-slot', 'prepend-inner');
          lead.setAttribute('data-olc-drop-target', '');
          lead.setAttribute('data-olc-slot-edge', 'true');
          appendEmptyPlus(lead);
          slotsRoot.appendChild(lead);
        }

        for (var j = 0; j < maxSlots; j++) {
          if (slots[j]) {
            renderOneSlot(j);
          } else if (j === firstEmpty) {
            renderOneSlot(j);
          }
        }

        if (showDualEdgeUI && isCanvasFull() && !hideEdgePlusWhenFull) {
          var trail = document.createElement('div');
          trail.className =
            'office-layout-configurator__slot office-layout-configurator__slot--append-at-max';
          trail.setAttribute('data-olc-slot', 'append-inner');
          trail.setAttribute('data-olc-drop-target', '');
          trail.setAttribute('data-olc-slot-edge', 'true');
          appendEmptyPlus(trail);
          slotsRoot.appendChild(trail);
        }

        slotsRoot.classList.toggle(
          'office-layout-configurator__slots--dual-edge-leading',
          showDualEdgeUI && !hideEdgePlusWhenFull
        );
        slotsRoot.classList.toggle(
          'office-layout-configurator__slots--edge-plus-hidden',
          hideEdgePlusWhenFull
        );
        slotsRoot.classList.toggle(
          'office-layout-configurator__slots--edge-plus-compact',
          compactEdgePlus
        );
        if (canvasEl) {
          canvasEl.classList.toggle(
            'office-layout-configurator__canvas--full-slots',
            hideEdgePlusWhenFull
          );
        }
      }

      if (canvasEl && (isCollapsedCanvas() || !slots.some(Boolean))) {
        canvasEl.classList.remove('office-layout-configurator__canvas--full-slots');
      }

      slotsRoot.classList.toggle('office-layout-configurator__slots--single', isCollapsedCanvas());
      renderSlotFootersRow();
      renderDimensionBar();
      if (canvasStageEl) {
        canvasStageEl.classList.toggle('is-active', slots.some(Boolean));
      }
      scheduleMobileCanvasLayout();
    }

    window.addEventListener('resize', scheduleMobileCanvasLayout, { passive: true });
    window.addEventListener('orientationchange', scheduleMobileCanvasLayout, { passive: true });

    /**
     * 在最左端插入一件（slots 内 prepend-inner），其余槽位右移；需最右槽为空。
     */
    function placeVariantPrepend(payload) {
      if (slots[maxSlots - 1]) {
        showMaxSlotsPopover();
        return false;
      }
      if (prependWouldDisplaceSideCabinetFromEdge()) {
        notifySideCabinetCannotBeMiddle();
        return false;
      }
      for (var i = maxSlots - 1; i >= 1; i--) {
        slots[i] = slots[i - 1];
      }
      slots[0] = {
        variantId: payload.variantId,
        productId: payload.productId,
        image: payload.image,
        title: payload.title,
        variantTitle: payload.title,
        price: payload.price,
        productTitle: payload.productTitle || '',
        skuTitle: payload.skuTitle || '',
        widthLabel: widthLabelFromVariantTitle(payload.title),
        sideCabinetOnly: !!payload.sideCabinetOnly,
        placementSeq: nextPlacementSeq++,
      };
      compactSlotsLeft();
      compactPlacementSeq();
      renderSlotCanvas();
      renderVariants();
      updatePrice();
      return true;
    }

    /**
     * 将 payload 放入指定槽位；slotIndex 为空时按收起态/边柜规则/首个空槽自动选址。
     */
    function placeVariant(payload, slotIndex) {
      var side = !!payload.sideCabinetOnly;
      var collapsed = isCollapsedCanvas();
      var idx;

      if (
        !collapsed &&
        (slotIndex === null || slotIndex === undefined) &&
        firstEmptyIndex() < 0
      ) {
        showMaxSlotsPopover();
        return false;
      }

      if (slotIndex !== null && slotIndex !== undefined) {
        idx = slotIndex;
      } else if (collapsed) {
        if (maxSlots <= 1) {
          idx = 0;
        } else if (side) {
          idx = maxSlots - 1;
        } else {
          idx = initialSlotIndex;
        }
      } else {
        if (side) {
          idx = firstEmptyEdgeIndex();
          if (idx < 0) {
            notifySideCabinetEdgeOnly();
            return false;
          }
        } else {
          idx = firstEmptyIndex();
          if (idx < 0) {
            showMaxSlotsPopover();
            return false;
          }
        }
      }

      if (idx < 0 || idx >= maxSlots) return false;

      if (fillingEmptyWouldTrapSideCabinetBetweenMains(idx)) {
        notifySideCabinetCannotBeMiddle();
        return false;
      }

      if (side && !isSideCabinetAllowedSlot(idx)) {
        notifySideCabinetEdgeOnly();
        return false;
      }
      slots[idx] = {
        variantId: payload.variantId,
        productId: payload.productId,
        image: payload.image,
        title: payload.title,
        variantTitle: payload.title,
        price: payload.price,
        productTitle: payload.productTitle || '',
        skuTitle: payload.skuTitle || '',
        widthLabel: widthLabelFromVariantTitle(payload.title),
        sideCabinetOnly: !!payload.sideCabinetOnly,
        placementSeq: nextPlacementSeq++,
      };
      compactSlotsLeft();
      compactPlacementSeq();
      renderSlotCanvas();
      renderVariants();
      updatePrice();
      return true;
    }

    function updatePrice() {
      var filled = slots.filter(Boolean);
      if (!filled.length) {
        priceEl.textContent = formatMoney(0, moneyFormat);
        priceNoteEl.textContent = config.strings.noSelection || '';
        addBtn.disabled = true;
        buyBtn.disabled = true;
        return;
      }
      var sum = filled.reduce(function (s, x) {
        return s + x.price;
      }, 0);
      priceEl.textContent = formatMoney(sum, moneyFormat);
      priceNoteEl.textContent = '';
      addBtn.disabled = false;
      buyBtn.disabled = false;
    }

    // 画布槽位：HTML5 拖放，仅接受本模块 MIME。
    slotsRoot.addEventListener('dragover', function (e) {
      var cell = e.target.closest('[data-olc-slot]');
      if (!cell || !slotsRoot.contains(cell)) return;
      var types = e.dataTransfer.types;
      var hasMime = false;
      for (var ti = 0; ti < types.length; ti++) {
        if (types[ti] === MIME) {
          hasMime = true;
          break;
        }
      }
      if (hasMime) {
        e.preventDefault();
        cell.classList.add('is-drag-over');
      }
    });
    slotsRoot.addEventListener('dragleave', function (e) {
      var cell = e.target.closest('[data-olc-slot]');
      if (cell && slotsRoot.contains(cell)) cell.classList.remove('is-drag-over');
    });
    slotsRoot.addEventListener('drop', function (e) {
      var cell = e.target.closest('[data-olc-slot]');
      if (!cell || !slotsRoot.contains(cell)) return;
      e.preventDefault();
      cell.classList.remove('is-drag-over');
      var rawSlot = cell.getAttribute('data-olc-slot');
      var raw = e.dataTransfer.getData(MIME);
      if (!raw) return;
      if (cell.classList.contains('is-filled')) {
        if (isCanvasFull()) {
          showMaxSlotsPopover();
        }
        return;
      }
      if (rawSlot === 'append-inner') {
        showMaxSlotsPopover();
        return;
      }
      if (isCanvasFull() && rawSlot !== 'prepend-inner') {
        showMaxSlotsPopover();
        return;
      }
      try {
        if (rawSlot === 'collapsed') {
          placeVariant(JSON.parse(raw), null);
        } else if (rawSlot === 'prepend-inner') {
          placeVariantPrepend(JSON.parse(raw));
        } else {
          placeVariant(JSON.parse(raw), parseInt(rawSlot, 10));
        }
      } catch (err) {}
    });

    // 点击空槽：打开选款弹窗；收起态 data-olc-slot=collapsed 时索引为 null。
    slotsRoot.addEventListener('click', function (e) {
      var slotEl = e.target.closest('.office-layout-configurator__slot');
      if (!slotEl || !slotsRoot.contains(slotEl)) return;
      if (slotEl.classList.contains('is-filled')) return;
      var raw = slotEl.getAttribute('data-olc-slot');
      if (raw === 'collapsed') {
        openModalPickSlot(null);
        return;
      }
      if (raw === 'prepend-inner') {
        if (isCanvasFull()) {
          showMaxSlotsPopover();
        } else if (prependWouldDisplaceSideCabinetFromEdge()) {
          notifySideCabinetCannotBeMiddle();
        } else {
          openModalPickSlot('prepend');
        }
        return;
      }
      if (raw === 'append-inner') {
        showMaxSlotsPopover();
        return;
      }
      var ix = parseInt(raw, 10);
      if (!isNaN(ix)) {
        if (fillingEmptyWouldTrapSideCabinetBetweenMains(ix)) {
          notifySideCabinetCannotBeMiddle();
        } else {
          openModalPickSlot(ix);
        }
      }
    });

    /** 从已填槽位生成购物车行项目（跳过空槽）。 */
    function getConfiguratorLineItems() {
      var items = slots.filter(Boolean);
      if (!items.length) return null;
      var sharedId = generateId();
      return buildLineItems(items, sharedId);
    }

    /** 加入购物车；购买整套时先清空购物车再添加并跳转结账。 */
    function submitCart(checkoutAfter) {
      var lineItems = getConfiguratorLineItems();
      if (!lineItems) return;
      addBtn.classList.add('is-loading');
      buyBtn.classList.add('is-loading');
      var base = window.Shopify.routes.root;
      var addPromise;
      if (checkoutAfter) {
        addPromise = fetch(base + 'cart/clear.js', { method: 'POST' })
          .then(function (r) {
            if (!r.ok) {
              return Promise.reject(new Error('Failed to clear cart'));
            }
            return r.json();
          })
          .then(function () {
            return fetch(base + 'cart/add.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ items: lineItems }),
            });
          });
      } else {
        addPromise = fetch(base + 'cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(cartPayload(lineItems)),
        });
      }
      addPromise
        .then(function (r) {
          return r.json();
        })
        .then(function (res) {
          if (res.status) {
            if (window.BMessage && window.BMessage.error) {
              window.BMessage.error({ content: res.description || res.message || 'Error' });
            } else {
              alert(res.description || res.message || 'Error');
            }
            return;
          }
          if (checkoutAfter) {
            window.location.href = base + 'checkout';
            return;
          }
          var miniCart = document.querySelector('mini-cart');
          if (miniCart && typeof miniCart.renderContents === 'function') {
            miniCart.renderContents(res, true);
          }
        })
        .catch(function (err) {
          var msg = err && err.message ? err.message : 'Error';
          if (window.BMessage && window.BMessage.error) {
            window.BMessage.error({ content: msg });
          } else {
            alert(msg);
          }
        })
        .finally(function () {
          addBtn.classList.remove('is-loading');
          buyBtn.classList.remove('is-loading');
        });
    }

    addBtn.addEventListener('click', function () {
      submitCart(false);
    });
    buyBtn.addEventListener('click', function () {
      submitCart(true);
    });

    /**
     * 主题或其它脚本调用；也可由 `office-layout-configurator:apply-preset` 触发。
     * detail.skus：按顺序对应各槽位；每项可为 Variant SKU 或 Variant ID（纯数字）。
     * 会将颜色切到第一项的颜色。
     */
    root._olcApplyPreset = function (detail) {
      var d = detail || {};
      var rawList = d.skus;
      if (!rawList || !rawList.length) return;
      var list = rawList
        .map(function (s) {
          return String(s || '').trim();
        })
        .filter(Boolean);
      if (!list.length) return;
      if (list.length > maxSlots) {
        notifyPresetError(
          'This setup has more cabinets than the canvas allows (max ' + maxSlots + ').'
        );
        return;
      }
      var rows = [];
      var i;
      for (i = 0; i < list.length; i++) {
        var row = findRowByPresetToken(list[i]);
        if (!row) {
          notifyPresetError('SKU or variant ID not found in this configurator: ' + list[i]);
          return;
        }
        if (!row.variant.available) {
          notifyPresetError('This item is unavailable: ' + list[i]);
          return;
        }
        if (row.sideCabinetOnly && i !== 0 && i !== list.length - 1) {
          notifyPresetError(
            'Side cabinet SKUs must be placed in the left or right slot order in your list.'
          );
          return;
        }
        rows.push(row);
      }
      var firstColor = optionValueAt(rows[0].variant, colorIdx);
      if (firstColor) {
        selectedColor = String(firstColor).trim();
      }
      renderSwatches();
      for (i = 0; i < maxSlots; i++) {
        slots[i] = null;
      }
      nextPlacementSeq = 1;
      compactPlacementSeq();
      for (i = 0; i < rows.length; i++) {
        var ok = placeVariant(rowPayloadFromRow(rows[i]), i);
        if (!ok) {
          return;
        }
      }
      if (d.scroll !== false) {
        requestAnimationFrame(function () {
          root.scrollIntoView({ behavior: 'smooth', block: 'start' });
        });
      }
    };

    /** 检查预设 SKU 列表是否均可售；供推荐配置等区块联动按钮状态。 */
    root._olcCheckPresetSkusAvailable = function (rawList) {
      if (!rawList || !rawList.length) {
        return { available: false, reason: 'empty' };
      }
      var list = rawList
        .map(function (s) {
          return String(s || '').trim();
        })
        .filter(Boolean);
      if (!list.length) {
        return { available: false, reason: 'empty' };
      }
      for (var i = 0; i < list.length; i++) {
        var row = findRowByPresetToken(list[i]);
        if (!row) {
          return { available: false, reason: 'not_found', token: list[i] };
        }
        if (!row.variant.available) {
          return { available: false, reason: 'unavailable', token: list[i] };
        }
      }
      return { available: true };
    };

    function findFirstRowForProductVariant(productId, variantId) {
      if (productId == null || productId === '') return null;
      if (variantId != null && variantId !== '') {
        for (var fi = 0; fi < flatRows.length; fi++) {
          var r = flatRows[fi];
          if (String(r.productId) !== String(productId)) continue;
          if (String(r.variant.id) === String(variantId)) return r;
        }
      }
      for (var fj = 0; fj < flatRows.length; fj++) {
        if (String(flatRows[fj].productId) === String(productId)) return flatRows[fj];
      }
      return null;
    }

    function stripOlcQueryParamsFromUrl() {
      try {
        var u = new URL(window.location.href);
        if (!u.searchParams.has('olc_product_id') && !u.searchParams.has('olc_variant_id')) return;
        u.searchParams.delete('olc_product_id');
        u.searchParams.delete('olc_variant_id');
        var qs = u.searchParams.toString();
        var next = u.pathname + (qs ? '?' + qs : '') + u.hash;
        window.history.replaceState({}, '', next);
      } catch (e) {}
    }

    /** 解析 ?olc_product_id= &olc_variant_id=，同步颜色、滚动右侧分组。 */
    function applyUrlProductContextFromQuery() {
      if (root.dataset.olcUrlContextApplied === '1') return;
      var params;
      try {
        params = new URLSearchParams(window.location.search);
      } catch (e) {
        return;
      }
      var pid = params.get('olc_product_id');
      if (!pid) return;
      root.dataset.olcUrlContextApplied = '1';

      var pMeta = findProductById(pid);
      if (!pMeta) {
        if (urlStripQueryAfterApply) stripOlcQueryParamsFromUrl();
        return;
      }

      var vid = params.get('olc_variant_id');
      var row = findFirstRowForProductVariant(pid, vid);
      if (!row || !row.variant) {
        if (urlStripQueryAfterApply) stripOlcQueryParamsFromUrl();
        return;
      }

      var c = optionValueAt(row.variant, colorIdx);
      if (c) {
        selectedColor = String(c).trim();
      }

      renderSwatches();
      renderVariants();
      renderSlotCanvas();
      updatePrice();

      if (urlAutoPlaceFirstSlot && row.variant.available) {
        var anyFilled = false;
        for (var si = 0; si < slots.length; si++) {
          if (slots[si]) {
            anyFilled = true;
            break;
          }
        }
        if (!anyFilled) {
          placeVariant(rowPayloadFromRow(row), null);
        }
      }

      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          var esc =
            typeof CSS !== 'undefined' && CSS.escape
              ? CSS.escape(String(pid))
              : String(pid).replace(/[^a-zA-Z0-9_-]/g, '');
          var cur = root.querySelector('[data-olc-sku-heading-current][data-product-id="' + esc + '"]');
          var wrap = cur && cur.closest('.office-layout-configurator__variants-sku-group');
          if (wrap) {
            wrap.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
            return;
          }
          var cards = root.querySelectorAll('.office-layout-configurator__variant-card');
          for (var ci = 0; ci < cards.length; ci++) {
            try {
              var raw = cards[ci].dataset.variantPayload;
              if (!raw) continue;
              var pj = JSON.parse(raw);
              if (String(pj.productId) === String(pid)) {
                cards[ci].scrollIntoView({ behavior: 'smooth', block: 'nearest' });
                break;
              }
            } catch (e) {}
          }
        });
      });

      if (urlStripQueryAfterApply) stripOlcQueryParamsFromUrl();
    }

    renderSwatches();
    renderVariants();
    renderSlotCanvas();
    updatePrice();
    applyUrlProductContextFromQuery();

    document.dispatchEvent(
      new CustomEvent('office-layout-configurator:ready', {
        bubbles: true,
        detail: { root: root },
      })
    );
  }

  /** 页面存在 DIY 区块时在 html 上打标，由 section less 覆盖全局导航 sticky。 */
  function refreshOlcDisableGlobalStickyHeader() {
    var has = !!document.querySelector('[data-office-layout-configurator]');
    document.documentElement.classList.toggle('olc-disable-global-sticky-header', has);
  }

  /** 页面加载时初始化页面上所有 DIY 配置器区块。 */
  function boot() {
    refreshOlcDisableGlobalStickyHeader();
    document.querySelectorAll('[data-office-layout-configurator]').forEach(initConfigurator);
    applyStoredCrossPagePreset();
  }

  function readStoredCrossPagePreset() {
    if (!window.sessionStorage) return null;
    try {
      var raw = window.sessionStorage.getItem(CROSS_PAGE_PRESET_STORAGE_KEY);
      if (!raw) return null;
      return JSON.parse(raw);
    } catch (e) {
      return null;
    }
  }

  function clearStoredCrossPagePreset() {
    if (!window.sessionStorage) return;
    try {
      window.sessionStorage.removeItem(CROSS_PAGE_PRESET_STORAGE_KEY);
    } catch (e) {}
  }

  function normalizeStoredCrossPagePreset(raw) {
    if (!raw || typeof raw !== 'object') return null;
    var skus = Array.isArray(raw.skus)
      ? raw.skus
          .map(function (item) {
            return String(item || '').trim();
          })
          .filter(Boolean)
      : [];
    if (!skus.length) return null;

    var createdAt = Number(raw.createdAt || 0);
    if (createdAt > 0 && Date.now() - createdAt > CROSS_PAGE_PRESET_MAX_AGE) {
      return null;
    }

    var detail = {
      skus: skus,
      scroll: raw.scroll !== false,
    };
    if (raw.targetId) {
      detail.targetId = String(raw.targetId).trim();
    }
    return detail;
  }

  function applyStoredCrossPagePreset() {
    var detail = normalizeStoredCrossPagePreset(readStoredCrossPagePreset());
    if (!detail) {
      clearStoredCrossPagePreset();
      return;
    }
    clearStoredCrossPagePreset();
    document.dispatchEvent(
      new CustomEvent('office-layout-configurator:apply-preset', {
        bubbles: true,
        detail: detail,
      })
    );
  }

  /** 跨区块预设：解析目标根节点后委托给 `_olcApplyPreset`。 */
  document.addEventListener('office-layout-configurator:apply-preset', function (ev) {
    var d = ev.detail || {};
    var skus = d.skus;
    if (!skus || !skus.length) return;

    var root = null;
    if (d.targetId) {
      root = document.getElementById(d.targetId);
      if (!root || !root.matches('[data-office-layout-configurator]')) {
        if (window.BMessage && typeof window.BMessage.warning === 'function') {
          window.BMessage.warning({
            content: 'Could not find a DIY module with this anchor ID.',
          });
        }
        return;
      }
    } else {
      root = document.querySelector('[data-office-layout-configurator]');
    }
    if (!root || typeof root._olcApplyPreset !== 'function') {
      if (window.BMessage && typeof window.BMessage.warning === 'function') {
        window.BMessage.warning({
          content: 'Could not find the cabinet configurator on this page.',
        });
      }
      return;
    }
    root._olcApplyPreset(d);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // 主题编辑器：section 加载或复制后重新初始化。
  document.addEventListener('shopify:section:load', function (ev) {
    var root = ev.target.querySelector && ev.target.querySelector('[data-office-layout-configurator]');
    if (root) initConfigurator(root);
    refreshOlcDisableGlobalStickyHeader();
  });

  document.addEventListener('shopify:section:unload', function (ev) {
    if (!ev.target || !ev.target.querySelector) return;
    if (!ev.target.querySelector('[data-office-layout-configurator]')) return;
    requestAnimationFrame(refreshOlcDisableGlobalStickyHeader);
  });
})();
